import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { authService } from '../services/authService';
import { can } from '../lib/permissions';
import NotFound from '../pages/NotFound';
import { APP_URL, LOGIN_URL } from '../config';
import { useFlag, useFlagsReady } from '../FlagProvider';

function getConfiguredOrigin(url) {
  if (!url) {
    return null;
  }

  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
}

// APP_URL (config.js) always resolves -- to an explicitly configured origin,
// or (unconfigured) to window.location.origin itself. That makes this
// comparison trivially true whenever unconfigured, which is a real change
// from the old build-time app-url variable (routinely unset, so this
// clause was routinely false): an unconfigured single-host deployment now
// defaults to "app host" here rather than falling through to the hardcoded
// hostname checks below. That default matches config.js's own philosophy --
// an unconfigured deployment should stay on the current host, not guess --
// and a configured two-host deployment (app.signalcue.ai vs signalcue.ai)
// still resolves correctly because APP_URL and the viewer's origin only
// agree on the actual app host. The hardcoded checks stay as a fallback for
// any hostname reachable before APP_URL is configured for it.
export function isSignalCueAppHost(hostname = window.location.hostname, origin = window.location.origin) {
  const configuredAppOrigin = getConfiguredOrigin(APP_URL);

  return configuredAppOrigin === origin ||
         hostname === 'app.signalcue.ai' ||
         hostname.includes('app.signalcue.ai') ||
         hostname.includes('signalcue-web-staging') ||
         hostname.includes('signalcue-web-probe') ||
         hostname.includes('localhost') ||
         hostname.includes('127.0.0.1');
}

// ProtectedRoute answers three questions in order: are you signed in, is this
// surface yours, and does this deployment serve it?
//
// The second answers with a 404, not a permissions message: a page you cannot
// open is, to you, a page that is not there, and a 404 is something a user can
// act on where "Forbidden" only invites a ticket about a page they will never be
// given. It is NOT a confidentiality measure — permissions.json ships the whole
// role→feature matrix inside the client bundle, so nothing about it is hidden
// from anyone. The API answers 403 on the same denial, because a real client bug
// has to stay diagnosable in the logs (spec §5).
//
// The third gives the same 404 for a different reason: a flag-off surface and a
// role denial are indistinguishable to whoever is looking at the page. A route
// with no `flag` prop never consults the flag machinery at all.
//
// It renders NotFound rather than redirecting: a redirect would turn a stale
// bookmark into a silent bounce that reads as a broken app.
//
// Design: docs/superpowers/specs/2026-08-08-role-feature-permissions-design.md
export default function ProtectedRoute({ children, feature, flag }) {
  const location = useLocation();
  // Both hooks run unconditionally. useFlag(undefined) is false and is never
  // consulted for a route with no flag, which is what keeps ungated routes
  // behaving exactly as they did.
  const flagsReady = useFlagsReady();
  const flagOn = useFlag(flag);

  // Check for token in URL params first (for cross-domain auth)
  const urlParams = new URLSearchParams(location.search);
  const tokenFromUrl = urlParams.get('token');
  const userFromUrl = urlParams.get('user');

  // If token is in URL, store it immediately (synchronously) before checking auth
  if (tokenFromUrl && userFromUrl && !authService.isAuthenticated()) {
    sessionStorage.setItem('signalcue_token', tokenFromUrl);
    sessionStorage.setItem('signalcue_user', userFromUrl);
    // Remove token from URL for security
    const newParams = new URLSearchParams(urlParams);
    newParams.delete('token');
    newParams.delete('user');
    const newSearch = newParams.toString();
    const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
    window.history.replaceState({}, document.title, newUrl);
  }

  if (!authService.isAuthenticated()) {
    // Check if we're on the app domain - if so, redirect to public login
    // If we're already on the public domain, show login
    const isAppDomain = isSignalCueAppHost();

    if (isAppDomain) {
      // Redirect to public login site
      window.location.href = `${LOGIN_URL}/login`;
      return null;
    }
    // If on public domain, redirect to login route
    return <Navigate to="/login" replace />;
  }

  // Signed in, but this surface is not theirs. A route with no feature is open to
  // every signed-in role (/help, /performance).
  if (feature && !can(authService.getUser()?.role || '', feature)) {
    return <NotFound />;
  }

  // The role check comes first on purpose: a role that is denied this surface
  // gets NotFound without waiting on configuration it cannot change the outcome
  // of.
  //
  // Waiting is not the same as denying. Every flag reads false until the config
  // answers, so denying here would render NotFound over a page the user is
  // entitled to on every refresh and every deep link.
  if (flag) {
    if (!flagsReady) {
      return <div className="min-h-screen" aria-busy="true" />;
    }
    if (!flagOn) {
      return <NotFound />;
    }
  }

  return children;
}
